import React, { useState, useMemo, useEffect } from 'react';
import { Plus, Trash2, Search, Edit2, FileText, Upload, X, AlertCircle } from 'lucide-react';
import API_BASE_URL from '../../../config/baseApi';
import STORAGE_URL from '../../../config/storageApi';
import InputField from 'components/fields/InputField';
import SelectField from 'components/fields/SelectField';

export default function Staff() {
  const [staffMembers, setStaffMembers] = useState([]);
  const [departments, setDepartments] = useState([
    'Front Desk', 'Housekeeping', 'Kitchen', 'Management', 'Security', 'Maintenance'
  ]);

  const [formData, setFormData] = useState({
    knownName: '',
    fullName: '',
    contactNo: '',
    address: '',
    department: '',
    idNo: '',
    religion: '',
    bloodGroup: '',
    emContactNo: '',
    accountNo: '',
    accountName: '',
    bank: '',
    branch: '',
    specialSkills: '',
    preWorked: '',
    joinedDate: '',
    currentlyEmployed: '',
    resignDate: '',
    reason: '',
    comments: ''
  });

  const [editingId, setEditingId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState(null);
  const [selectedImageFile, setSelectedImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [showEntries, setShowEntries] = useState('10');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const employmentStatus = ['Yes', 'No'];

  // Fetch staff on component mount
  useEffect(() => {
    fetchStaff();
    fetchDepartments();
  }, []);

  // Fetch all staff members
  const fetchStaff = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/api/staff`);
      const data = await response.json();
      
      if (data.success) {
        setStaffMembers(data.data);
      } else {
        setError(data.message || 'Failed to fetch staff members');
      }
    } catch (err) {
      setError('Failed to connect to the server');
      console.error('Error fetching staff:', err);
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments from API
  const fetchDepartments = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/staff/departments`);
      const data = await response.json();
      
      if (data.success && data.data.length > 0) {
        setDepartments(data.data);
        console.log("Departments fetched:", data.data);
      }
    } catch (err) {
      console.error('Error fetching departments:', err);
      // Keep default departments if API fails
    }
  };

  const filteredStaff = useMemo(() => {
    return staffMembers.filter(member =>
      member.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.contact_no.includes(searchTerm) ||
      member.department.toLowerCase().includes(searchTerm.toLowerCase()) ||
      member.id_no.includes(searchTerm)
    );
  }, [staffMembers, searchTerm]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAddStaff = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName.trim() || !formData.knownName.trim() || !formData.department) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const url = editingId 
        ? `${API_BASE_URL}/api/staff/update/${editingId}`
        : `${API_BASE_URL}/api/staff/add`;
      
      const method = editingId ? 'PUT' : 'POST';

      const requestBody = {
        known_name: formData.knownName,
        full_name: formData.fullName,
        contact_no: formData.contactNo,
        address: formData.address,
        department: formData.department,
        id_no: formData.idNo,
        religion: formData.religion,
        blood_group: formData.bloodGroup,
        em_contact_no: formData.emContactNo,
        account_no: formData.accountNo,
        account_name: formData.accountName,
        bank: formData.bank,
        branch: formData.branch,
        special_skills: formData.specialSkills,
        pre_worked: formData.preWorked,
        joined_date: formData.joinedDate,
        currently_employed: formData.currentlyEmployed,
        resign_date: formData.resignDate || null,
        reason: formData.reason,
        comments: formData.comments
      };

      const response = await fetch(url, {
        method: method,
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (data.success) {
        await fetchStaff(); // Refresh the list
        resetForm();
        alert(data.message);
      } else {
        setError(data.message || 'Operation failed');
        if (data.errors) {
          const errorMessages = Object.values(data.errors).flat().join(', ');
          alert(errorMessages);
        }
      }
    } catch (err) {
      setError('Failed to save staff member');
      console.error('Error saving staff:', err);
      alert('Failed to save staff member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      knownName: '',
      fullName: '',
      contactNo: '',
      address: '',
      department: '',
      idNo: '',
      religion: '',
      bloodGroup: '',
      emContactNo: '',
      accountNo: '',
      accountName: '',
      bank: '',
      branch: '',
      specialSkills: '',
      preWorked: '',
      joinedDate: '',
      currentlyEmployed: '',
      resignDate: '',
      reason: '',
      comments: ''
    });
    setEditingId(null);
  };

  const handleEditStaff = (staff) => {
    setFormData({
      knownName: staff.known_name,
      fullName: staff.full_name,
      contactNo: staff.contact_no,
      address: staff.address,
      department: staff.department,
      idNo: staff.id_no,
      religion: staff.religion,
      bloodGroup: staff.blood_group,
      emContactNo: staff.em_contact_no,
      accountNo: staff.account_no,
      accountName: staff.account_name,
      bank: staff.bank,
      branch: staff.branch,
      specialSkills: staff.special_skills || '',
      preWorked: staff.pre_worked || '',
      joinedDate: staff.joined_date,
      currentlyEmployed: staff.currently_employed,
      resignDate: staff.resign_date || '',
      reason: staff.reason || '',
      comments: staff.comments || ''
    });
    setEditingId(staff.id);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteStaff = async (id) => {
    if (!window.confirm('Are you sure you want to delete this staff member?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_BASE_URL}/api/staff/delete/${id}`, {
        method: 'DELETE',
        headers: {
          'Accept': 'application/json'
        }
      });

      const data = await response.json();

      if (data.success) {
        await fetchStaff(); // Refresh the list
        alert(data.message);
      } else {
        setError(data.message || 'Failed to delete staff member');
        alert(data.message || 'Failed to delete staff member');
      }
    } catch (err) {
      setError('Failed to delete staff member');
      console.error('Error deleting staff:', err);
      alert('Failed to delete staff member. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedImageFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleUploadImage = async () => {
    if (!selectedStaffId || !selectedImageFile) {
      alert('Please select an image');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('image', selectedImageFile);

      const response = await fetch(`${API_BASE_URL}/api/staff/${selectedStaffId}/upload-image`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (data.success) {
        await fetchStaff(); // Refresh the list
        setShowImageModal(false);
        setImagePreview(null);
        setSelectedImageFile(null);
        setSelectedStaffId(null);
        alert(data.message);
      } else {
        setError(data.message || 'Failed to upload image');
        alert(data.message || 'Failed to upload image');
      }
    } catch (err) {
      setError('Failed to upload image');
      console.error('Error uploading image:', err);
      alert('Failed to upload image. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const openImageModal = (staffId) => {
    if (!staffId) {
      alert('Please select a staff member from the table');
      return;
    }
    setSelectedStaffId(staffId);
    setShowImageModal(true);
  };

  const closeImageModal = () => {
    setShowImageModal(false);
    setImagePreview(null);
    setSelectedImageFile(null);
    setSelectedStaffId(null);
  };

  const getImageUrl = (imagePath) => {
    if (!imagePath) return null;
    return `${STORAGE_URL}/${imagePath}`;
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 p-4 md:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
            <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-800 dark:text-red-200">Error</h3>
              <p className="text-sm text-red-700 dark:text-red-300 mt-1">{error}</p>
            </div>
            <button 
              onClick={() => setError(null)}
              className="text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-200"
            >
              ×
            </button>
          </div>
        )}

        <div className="space-y-6">
          {/* Add/Edit Staff Form */}
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-lg border border-gray-200 dark:border-gray-700">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-6 flex items-center gap-2">
              <Plus className="w-6 h-6 text-blue-500" />
              {editingId ? 'Edit Staff Member' : 'Add New Staff Member'}
            </h2>
            <form onSubmit={handleAddStaff} className="space-y-4">
              {/* Personal Information */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Personal Information</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <InputField label="Known Name" name="knownName" value={formData.knownName} onChange={handleInputChange} placeholder="e.g., Ali" required />
                  <InputField label="Full Name" name="fullName" value={formData.fullName} onChange={handleInputChange} placeholder="e.g., Ali Ahmed Khan" required />
                  <InputField label="Contact No" name="contactNo" value={formData.contactNo} onChange={handleInputChange} placeholder="e.g., 03001234567" required />
                  <InputField label="Address" name="address" value={formData.address} onChange={handleInputChange} placeholder="e.g., Karachi, Pakistan" required />
                </div>
              </div>

              {/* Department & ID */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Department & Identification</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <SelectField label="Department" name="department" value={formData.department} onChange={handleInputChange} options={departments} required />
                  <InputField label="ID No (NIC)" name="idNo" value={formData.idNo} onChange={handleInputChange} placeholder="e.g., 42101-1234567-5" required />
                  <InputField label="Religion" name="religion" value={formData.religion} onChange={handleInputChange} placeholder="e.g., Islam" required />
                  <InputField label="Blood Group" name="bloodGroup" value={formData.bloodGroup} onChange={handleInputChange} placeholder="e.g., O+" required />
                </div>
              </div>

              {/* Contact & Emergency */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Emergency Contact</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <InputField label="Emergency Contact No" name="emContactNo" value={formData.emContactNo} onChange={handleInputChange} placeholder="e.g., 03009876543" required />
                </div>
              </div>

              {/* Bank Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Bank Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <InputField label="Account No" name="accountNo" value={formData.accountNo} onChange={handleInputChange} placeholder="e.g., 123456789" required />
                  <InputField label="Account Name" name="accountName" value={formData.accountName} onChange={handleInputChange} placeholder="e.g., Ali Ahmed" required />
                  <InputField label="Bank" name="bank" value={formData.bank} onChange={handleInputChange} placeholder="e.g., HBL" required />
                  <InputField label="Branch" name="branch" value={formData.branch} onChange={handleInputChange} placeholder="e.g., Karachi Main" required />
                </div>
              </div>

              {/* Skills & Experience */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Skills & Experience</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <InputField label="Special Skills" name="specialSkills" value={formData.specialSkills} onChange={handleInputChange} placeholder="e.g., Customer Service, English" />
                  <InputField label="Previously Worked" name="preWorked" value={formData.preWorked} onChange={handleInputChange} placeholder="e.g., XYZ Hotel" />
                </div>
              </div>

              {/* Employment Details */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Employment Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <InputField label="Joined Date" name="joinedDate" type="date" value={formData.joinedDate} onChange={handleInputChange} required />
                  <SelectField label="Currently Employed" name="currentlyEmployed" value={formData.currentlyEmployed} onChange={handleInputChange} options={employmentStatus} required />
                  <InputField label="Resign Date" name="resignDate" type="date" value={formData.resignDate} onChange={handleInputChange} />
                  <InputField label="Reason" name="reason" value={formData.reason} onChange={handleInputChange} placeholder="Reason for resignation" />
                </div>
              </div>

              {/* Comments */}
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Additional Information</h3>
                <div className="grid grid-cols-1 gap-4">
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-900 dark:text-gray-300 mb-2">Comments</label>
                    <textarea
                      name="comments"
                      value={formData.comments}
                      onChange={handleInputChange}
                      placeholder="Add any additional comments"
                      rows="4"
                      className="px-4 py-2.5 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                    />
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row gap-4 justify-center py-6">
                <button
                  type="submit"
                  disabled={loading}
                  className="px-10 py-3 bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-700 hover:to-blue-800 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? 'Processing...' : editingId ? 'Update Staff' : 'Add Staff'}
                </button>
                <button
                  type="button"
                  onClick={resetForm}
                  className="px-10 py-3 bg-gray-700 hover:bg-gray-600 text-white font-semibold rounded-lg transition-all duration-200 shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
                >
                  Reset
                </button>
              </div>
            </form>
          </div>

          {/* Staff Table */}
          <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
            {/* Table Header */}
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <h2 className="text-xl font-bold text-gray-900 dark:text-white">Staff List</h2>
                <div className="flex flex-col sm:flex-row gap-4 sm:items-center">
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-700 dark:text-gray-400">Show</span>
                    <select
                      value={showEntries}
                      onChange={(e) => setShowEntries(e.target.value)}
                      className="px-3 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="10">10</option>
                      <option value="25">25</option>
                      <option value="50">50</option>
                    </select>
                    <span className="text-sm text-gray-700 dark:text-gray-400">entries</span>
                  </div>
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder="Search staff..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 pr-4 py-1.5 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-900 dark:text-white text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50 dark:bg-gray-700/50">
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">#</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Full Name</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Image</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Contact</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Department</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">NIC</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Blood Group</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Employed</th>
                    <th className="px-6 py-4 text-left text-xs font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wider">Action</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                  {loading ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center text-gray-600 dark:text-gray-400">
                        <div className="flex flex-col items-center justify-center">
                          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mb-3"></div>
                          <p className="text-sm">Loading staff...</p>
                        </div>
                      </td>
                    </tr>
                  ) : filteredStaff.length === 0 ? (
                    <tr>
                      <td colSpan="9" className="px-6 py-12 text-center text-gray-600 dark:text-gray-400">
                        <div className="flex flex-col items-center justify-center">
                          <FileText className="w-12 h-12 text-gray-400 dark:text-gray-600 mb-3" />
                          <p className="text-sm">No staff members found</p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    filteredStaff.map((member, index) => (
                      <tr key={member.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors">
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 font-medium">{index + 1}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300 font-medium">{member.full_name}</td>
                        <td className="px-6 py-4 text-sm">
                          <div className="flex items-center gap-2">
                            {member.image ? (
                              <img src={getImageUrl(member.image)} alt="Staff" className="w-12 h-12 rounded-lg object-cover" />
                            ) : (
                              <span className="text-gray-400">No image</span>
                            )}
                            <button
                              onClick={() => openImageModal(member.id)}
                              className="p-1 text-green-500 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
                              title="Upload image"
                            >
                              <Upload className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">{member.contact_no}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">{member.department}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">{member.id_no}</td>
                        <td className="px-6 py-4 text-sm text-gray-900 dark:text-gray-300">{member.blood_group}</td>
                        <td className="px-6 py-4 text-sm">
                          <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                            member.currently_employed === 'Yes'
                              ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                              : 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300'
                          }`}>
                            {member.currently_employed}
                          </span>
                        </td>
                        <td className="px-6 py-4 text-sm space-x-2 flex">
                          <button
                            onClick={() => handleEditStaff(member)}
                            className="p-2 text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded-lg transition-colors"
                            disabled={loading}
                          >
                            <Edit2 className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDeleteStaff(member.id)}
                            className="p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                            disabled={loading}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>

        {/* Image Upload Modal */}
        {showImageModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full">
              <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                <h3 className="text-xl font-bold text-gray-900 dark:text-white">Upload Image</h3>
                <button
                  onClick={closeImageModal}
                  className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>
              <div className="p-6 space-y-4">
                <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-6">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageSelect}
                    className="w-full"
                  />
                </div>
                {imagePreview && (
                  <div className="mt-4">
                    <img src={imagePreview} alt="Preview" className="w-full h-48 object-cover rounded-lg" />
                  </div>
                )}
              </div>
              <div className="flex gap-3 p-6 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={closeImageModal}
                  className="flex-1 px-4 py-2 bg-gray-200 dark:bg-gray-700 hover:bg-gray-300 dark:hover:bg-gray-600 text-gray-900 dark:text-white font-medium rounded-lg transition-colors"
                >
                  Cancel
                </button>
                <button
                  onClick={handleUploadImage}
                  disabled={!selectedImageFile || loading}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Uploading...' : 'Upload'}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
          <p className="text-center text-sm text-gray-600 dark:text-gray-400">
            Copyright © 2026. All right reserved.
          </p>
        </div>
      </div>
    </div>
  );
}